import { toLower } from 'lodash/fp';
import { Component, createRef, forwardRef } from 'react';
import { connect } from 'react-redux';
import { Stack, StackProps } from '@optimus-web/core';
import { Target } from '@mews/enums';
import { ApplicationState } from '@mews-commander/types';
import { FeatureFlagService, withFeatureFlagService } from '@mews/framework';
import { backendComponentClickEventObservable } from '@mews-commander/ui';
import { createUrlFromPath, getPathFromUrl, openInBrowserTab } from '@mews-commander/utils';
import { navigateInModal, openModal } from '../app/actions/common';
import { getRouterBaseUrl, getServerBasePath } from '../app/reducers/configuration';
import { getRouterBasePathRegex } from '../navigation';
import { SubmitMetaFormEvent } from './SubmitMetaFormEvent';

interface ModalManagerEventHandlerProps {
    children: React.ReactNode;
    serverBasePath: string;
    routerBasePath: string;
    routerBasePathRegex: RegExp;
    navigateInModal: typeof navigateInModal;
    openModal: typeof openModal;
    onPostFormSubmit: ({ url, data }: { url: string; data: FormData; shouldHardReload: boolean }) => void;
    featureFlagService?: FeatureFlagService;
}
class ModalManagerEventHandler extends Component<ModalManagerEventHandlerProps> {
    ref = createRef<HTMLDivElement>();
    lastClickedMenuItem?: Element;
    lastClickParentForm?: HTMLFormElement;

    componentDidMount() {
        backendComponentClickEventObservable.subscribe(this.handleBackendComponentReroutedModalClick);
        if (this.ref.current) {
            this.ref.current.addEventListener('submit', this.handleFormSubmit);
            this.ref.current.addEventListener(SubmitMetaFormEvent.type, this.handleMetaFormSubmit);
        }
    }
    componentWillUnmount() {
        backendComponentClickEventObservable.unsubscribe(this.handleBackendComponentReroutedModalClick);
        if (this.ref.current) {
            this.ref.current.removeEventListener('submit', this.handleFormSubmit);
            this.ref.current.removeEventListener(SubmitMetaFormEvent.type, this.handleMetaFormSubmit);
        }
    }

    handleMetaFormSubmit = (event: Event) => {
        event.stopPropagation();
        if (event instanceof SubmitMetaFormEvent) {
            this.props.onPostFormSubmit({ ...event.detail, shouldHardReload: false });
        } else {
            throw new Error(`Invalid event type`);
        }
    };

    handleFormSubmit = (e: Event) => {
        const { target: form } = e;
        if (!form || !isHTMLFormElement(form)) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const formData = new FormData(form);
        /**
         * Some forms have multiple submits and those additionals are meant to add some data to formData.
         * Typical example are reports (export and export schedule buttons add boolean flags to the formData).
         * The lastClickedButton is set in click handler as there is no other direct way how to find it.
         * (There is a submitter property on SubmitEvent that is however not supported by some browsers.)
         */
        const { lastClickedMenuItem, lastClickParentForm } = this;
        if (lastClickedMenuItem && lastClickParentForm === form) {
            const key = lastClickedMenuItem.getAttribute('name');
            const value = lastClickedMenuItem.getAttribute('value');
            if (key && value) {
                formData.set(key, value);
            }
            this.lastClickedMenuItem = undefined;
            this.lastClickParentForm = undefined;
        }
        const method = form.getAttribute('method') || 'post';
        const action = form.getAttribute('action') || '';
        if (toLower(method) === 'get') {
            // URLSearchParams is not typed correctly, see: https://github.com/microsoft/TypeScript/issues/30584
            const searchParams = new URLSearchParams(Array.from(formData) as string[][]);
            const parsedUrl = createUrlFromPath(action);
            searchParams.forEach((value, key) => parsedUrl.searchParams.append(key, value));

            const url = getPathFromUrl(parsedUrl);
            this.props.navigateInModal(url);
        } else if (toLower(method) === 'post') {
            if (form.getAttribute('submitted') === 'true') {
                return;
            }
            form.setAttribute('submitted', 'true');
            const shouldHardReload = form.classList.contains('modal-no');
            this.props.onPostFormSubmit({ url: action, data: formData, shouldHardReload });
        }
    };

    handleAnchorClick = (targetElement: HTMLElement, e: React.MouseEvent<HTMLDivElement>) => {
        const url = targetElement.getAttribute('href');
        const target = targetElement.getAttribute('target');
        const openInNewWindow = targetElement.getAttribute('data-new-tab') === '';
        const keyboardEvent = e as unknown as KeyboardEvent;
        const commandKeyPressed = keyboardEvent.ctrlKey || keyboardEvent.metaKey;

        if (url && openInNewWindow) {
            openInBrowserTab(url);
            return;
        }
        if (!url || (target && target.toLowerCase() === Target.Blank) || commandKeyPressed) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        this.props.navigateInModal(url);
    };

    handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const { target } = e;
        if (isHTMLElement(target)) {
            const closestAnchor = target.closest('a');
            if (closestAnchor) {
                /**
                 * Without ModalManager, there's a double navigation if the BE template is calling openModal.
                 * However, this click handler is necessary for raw <a> links in BE templates, with no JS connected to them,
                 * and there's still a few places relying on this.
                 */
                this.handleAnchorClick(closestAnchor, e);
                return;
            }
            const closestMenuItem = target.closest('[data-test-id="report-menuitem"]');
            if (closestMenuItem) {
                this.lastClickedMenuItem = closestMenuItem;
                this.lastClickParentForm = target.closest('form') || undefined;
                if (this.lastClickParentForm) {
                    const event = new Event('submit', { bubbles: true, cancelable: true });
                    this.lastClickParentForm.dispatchEvent(event);
                }
            }
        }
    };
    handleBackendComponentReroutedModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isHTMLElement(e.target) && this.ref.current?.contains(e.target)) {
            this.handleModalClick(e);
        }
    };
    render() {
        return (
            <ContentWrapper ref={this.ref} onClick={this.handleModalClick}>
                {this.props.children}
            </ContentWrapper>
        );
    }
}

export const ContentWrapper = forwardRef<HTMLDivElement, StackProps & React.HTMLAttributes<HTMLDivElement>>(
    (props, ref) => <Stack fill vertical spacing="0" ref={ref} {...props} />
);

const isHTMLElement = (element: EventTarget): element is HTMLElement => (element as HTMLElement).tagName !== undefined;
const isHTMLFormElement = (element: EventTarget): element is HTMLFormElement =>
    (element as HTMLFormElement).submit !== undefined;
export default connect<
    Pick<ModalManagerEventHandlerProps, 'serverBasePath' | 'routerBasePath' | 'routerBasePathRegex'>,
    Pick<ModalManagerEventHandlerProps, 'openModal' | 'navigateInModal'>,
    Pick<ModalManagerEventHandlerProps, 'onPostFormSubmit' | 'children'>,
    ApplicationState
>(
    (state) => ({
        serverBasePath: getServerBasePath(),
        routerBasePath: getRouterBaseUrl(),
        routerBasePathRegex: getRouterBasePathRegex(state),
    }),
    { openModal, navigateInModal }
    // @ts-expect-error $TSFixMeError: Argument of type 'typeof ModalManagerEventHandler' is not assignable to parameter of type 'ComponentType<Matching<{ curr...
)(withFeatureFlagService(ModalManagerEventHandler));
